import type { Source } from '@/types/chat-v2'
import type { File } from '@/types/File'
import type { UUID } from '@/types/Id'

export type WikiSourceFileNameById = Partial<Record<UUID, string>>

export interface ResolvedWikiSourceFile {
  fileId: UUID
  file: File | null
  label: string
  isDeleted: boolean
}

export const buildDeletedWikiSourceFileIds = ({
  sourceFileIds,
  loadedFiles,
}: {
  sourceFileIds: UUID[]
  loadedFiles: File[]
}): UUID[] => {
  const loadedFileIds = new Set(loadedFiles.map((file) => file.id))
  return sourceFileIds.filter((fileId) => !loadedFileIds.has(fileId))
}

export const buildWikiSourceFileNameById = ({
  sources,
  loadedFiles = [],
}: {
  sources: Iterable<Source>
  loadedFiles?: Iterable<File>
}): WikiSourceFileNameById => {
  const sourceFileNameById: WikiSourceFileNameById = {}

  for (const file of loadedFiles) {
    const fileName = file.name.trim()
    if (!fileName) continue
    sourceFileNameById[file.id] = fileName
  }

  for (const source of sources) {
    if (source.source_type !== 'file') continue

    const fileName = source.file_name?.trim()
    if (!fileName || sourceFileNameById[source.file_id]) continue

    sourceFileNameById[source.file_id] = fileName
  }

  return sourceFileNameById
}

export const isDeletedWikiSourceFile = (
  fileId: UUID,
  deletedSourceFileIds: ReadonlySet<UUID>,
): boolean => {
  return deletedSourceFileIds.has(fileId)
}

export const getWikiSourceFileLabel = ({
  fileId,
  file,
  sourceFileNameById,
  deletedSourceFileIds,
  missingAvailableLabel = 'Document',
  missingDeletedLabel = 'Deleted file',
}: {
  fileId: UUID
  file: File | null | undefined
  sourceFileNameById: WikiSourceFileNameById
  deletedSourceFileIds: ReadonlySet<UUID>
  missingAvailableLabel?: string
  missingDeletedLabel?: string
}): string => {
  return (
    file?.name ??
    sourceFileNameById[fileId] ??
    (isDeletedWikiSourceFile(fileId, deletedSourceFileIds)
      ? missingDeletedLabel
      : missingAvailableLabel)
  )
}

export const buildResolvedWikiSourceFiles = ({
  sourceFileIds,
  filesById,
  sourceFileNameById,
  deletedSourceFileIds,
}: {
  sourceFileIds: UUID[]
  filesById: Map<UUID, File>
  sourceFileNameById: WikiSourceFileNameById
  deletedSourceFileIds: ReadonlySet<UUID>
}): ResolvedWikiSourceFile[] => {
  return sourceFileIds.map((fileId) => {
    const file = filesById.get(fileId) ?? null
    const isDeleted = isDeletedWikiSourceFile(fileId, deletedSourceFileIds)

    return {
      fileId,
      file,
      label: getWikiSourceFileLabel({
        fileId,
        file,
        sourceFileNameById,
        deletedSourceFileIds,
      }),
      isDeleted,
    }
  })
}
